import React, { useState, useEffect } from "react";

import Select from "react-select";
import { isEmpty } from "lodash";

import { Trans, useTranslation } from "react-i18next";

import LookupsAPI from "../../API/lookups.api";
import SimpleNotification from "../Notifications/SimpleNotification";
import UsersApi from "../../API/users.api";
import {
    getReactSelectSmallStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import { toCamelCase } from "../../utils/Helpers";
import InformationTooltip from "../Tooltips/InformationTooltip";
import { getReactSelectStyling } from "../../utils/ReactSelectStyles";

const ModelAccess = ({
    modelUserAccesses,
    modelOrganizationAccesses,
    createdBy,
    selectedUsers,
    setSelectedUsers,
    selectedOrganizations,
    setSelectedOrganizations,
    mappedUserAccesses,
    setMappedUserAccesses,
    creatorsDictionary,
    creatorsDictionarySetter,
    resetForm,
}) => {
    const [organizations, setOrganizations] = useState([]);
    const [organizationOptions, setOrganizationOptions] = useState([]);
    const [isDeletedOrganization, setIsDeletedOrganization] = useState([]);
    const [users, setUsers] = useState([]);
    const [creator, setCreator] = useState(null);
    const [usersCount, setUsersCount] = useState(1);
    const [organizationCount, setOrganizationCount] = useState(0);
    const [inferiorUsers, setInferiorUsers] = useState([]);
    const [mappedOrganizationAccesses, setMappedOrganizationAccesses] =
        useState({});

    const { t } = useTranslation();

    const headers = [t("email"), t("affiliate"), t("permission"), t("remove")];
    const userAccess = {
        read: { label: t("read"), value: "Read" },
        write: {
            label: t("readWrite"),
            value: "Read/Write",
        },
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        if (resetForm) {
            setMappedOrganizationAccesses({});
            setIsDeletedOrganization([]);
            setSelectedUsers([]);
            setSelectedOrganizations([]);
            setUsersCount(1);
            setOrganizationCount(0);
        }
    }, [resetForm]);

    useEffect(() => {
        if (creatorsDictionary && createdBy in creatorsDictionary) {
            creatorSetter(creatorsDictionary[createdBy]);
        } else {
            if (createdBy) {
                fetchCreator().then((result) => {
                    if (creatorsDictionarySetter) {
                        creatorsDictionarySetter({
                            ...creatorsDictionary,
                            [result.email]: result,
                        });
                    }
                    creatorSetter(result);
                });
            }
        }
    }, [createdBy]);

    useEffect(() => {
        if (isEmpty(inferiorUsers)) {
            fetchUsers();
        }
    }, [modelUserAccesses]);

    useEffect(() => {
        const mappedAccess = isEmpty(modelUserAccesses)
            ? []
            : mapUserAccesses();
        positionUsers(mappedAccess, inferiorUsers);
    }, [inferiorUsers, modelUserAccesses]);

    useEffect(() => {
        const mappedAccess = isEmpty(modelOrganizationAccesses)
            ? []
            : mapOrganizationAccesses();

        positionOrganization(mappedAccess, organizations);
    }, [modelOrganizationAccesses, organizations]);

    useEffect(() => {
        setUsers((prevUsers) => {
            return prevUsers.map((user) => ({
                ...user,
                label: mappedOrganizationAccesses[user.value.organization.id]
                    ? getInheretedAccessStyle(user.value)
                    : getPlainUserEmail(user.value),
            }));
        });
    }, [mappedOrganizationAccesses]);

    const mapOrganizationAccesses = () => {
        let mappedAccesses = modelOrganizationAccesses.reduce(
            (accesses, currentAccess) => {
                accesses[currentAccess.organizationId] = userAccess.read;
                return accesses;
            },
            {}
        );
        setMappedOrganizationAccesses(mappedAccesses);
        return mappedAccesses;
    };

    const mapUserAccesses = () => {
        //Map user accesses to dictionary for better access
        let mappedAccesses = modelUserAccesses.reduce(
            (accesses, currentAccess) => {
                accesses[currentAccess.userId] = currentAccess.write
                    ? userAccess.write
                    : userAccess.read;
                return accesses;
            },
            {}
        );

        setMappedUserAccesses(mappedAccesses);
        return mappedAccesses;
    };

    const fetchOrganizations = async () => {
        const result = await LookupsAPI.getOrganizations();
        setOrganizations(
            result.map((organization) => ({
                value: organization.id,
                label: organization.name,
            }))
        );
    };

    const creatorSetter = (creator) => {
        creator &&
            setCreator({
                label: creator.email,
                value: { ...creator, creator: true },
            });
    };

    const fetchCreator = async () => {
        if (createdBy) {
            const result = await UsersApi.getUserByEmail(createdBy);
            return result;
        }
    };

    const fetchUsers = async () => {
        const result = await LookupsAPI.getInferiorUsers();
        setInferiorUsers(result);
    };

    const positionOrganization = (OrganizationAccesses, organizations) => {
        const { selected, options } = organizations.reduce(
            (accum, current) => {
                OrganizationAccesses[current.value]
                    ? accum.selected.push(current)
                    : accum.options.push(current);
                return accum;
            },
            {
                selected: [],
                options: [],
            }
        );

        setOrganizationCount(selected.length);
        setOrganizationOptions(options);
        setSelectedOrganizations(selected);
    };

    const positionUsers = async (mappedAccess, fetchedUsers) => {
        try {
            const [usersWithAccess, usersOptions] = fetchedUsers.reduce(
                (accum, user) => {
                    //If user got access push to usersWithAccess else push to usersOptions
                    accum[mappedAccess && mappedAccess[user.id] ? 0 : 1].push({
                        label: user.email,
                        value: {
                            ...user,
                            value: { ...user.value, isDeleted: false },
                        },
                    });
                    return accum;
                },
                [[], []]
            );

            setUsersCount(usersWithAccess.length + 1);
            setSelectedUsers(usersWithAccess);
            setUsers(usersOptions);
        } catch (error) {
            console.log(error);
        }
    };

    const handleOnUsersChange = (user) => {
        if (user) {
            //Add the selected user to selectedUsers
            setSelectedUsers((prevSelectedUsers) => {
                const newSelectedUsers = [...prevSelectedUsers];
                if (previousAccess(user.value.id)) {
                    //if the user already exists in fetched accesses then set isDeleted to false
                    newSelectedUsers.find(
                        (stepUser) => stepUser.value.id === user.value.id
                    ).value.isDeleted = false;
                } else {
                    //else unshift the selected user to selected users
                    newSelectedUsers.unshift(user);
                }

                return newSelectedUsers;
            });

            //Remove the selected user from users options
            setUsers((prevUsers) => {
                return prevUsers.filter((singleUser) => {
                    const value = singleUser.value;
                    //filter user by id and return only different ids
                    if (value.id !== user.value.id) {
                        return singleUser;
                    }
                });
            });

            setUsersCount((prevUsersCount) => prevUsersCount + 1);
            setMappedUserAccesses({
                ...mappedUserAccesses,
                [user.value?.id]: userAccess.read,
            });
        }
    };

    const organizationChangeSelect = (action) => {
        const addedOrganization = action.option;

        if (mappedOrganizationAccesses[action.option.value]) {
            setSelectedOrganizations((prevSelectedOrganizations) => {
                return prevSelectedOrganizations.map((organization) => {
                    return organization.value === action.option.value
                        ? { ...organization, isDeleted: false }
                        : organization;
                });
            });
            setIsDeletedOrganization((prevDeleted) => {
                return prevDeleted.filter(
                    (organization) => organization.value !== action.option.value
                );
            });
        } else {
            setSelectedOrganizations((prevSelectedOrganizations) => [
                ...prevSelectedOrganizations,
                {
                    value: addedOrganization?.value,
                    label: addedOrganization?.label,
                },
            ]);
        }
        setOrganizationCount((prevCount) => prevCount + 1);
    };

    const organizationChangeRemove = (action) => {
        const removedOrganization = action.removedValue;

        if (mappedOrganizationAccesses[action.removedValue.value]) {
            setSelectedOrganizations((prevSelectedOrganizations) => {
                return prevSelectedOrganizations.map((organization) => {
                    return organization.value === action.removedValue.value
                        ? { ...organization, isDeleted: true }
                        : organization;
                });
            });
            setIsDeletedOrganization((prevDeleted) => {
                return [...prevDeleted, action.removedValue];
            });
        } else {
            setSelectedOrganizations((prevSelectedOrganizations) => {
                return prevSelectedOrganizations.filter((organization) => {
                    return organization.value !== removedOrganization.value;
                });
            });
        }
        setOrganizationCount((prevCount) => prevCount - 1);
    };

    const handleOrganizationChange = (_, action) => {
        if (action.action === "select-option") {
            organizationChangeSelect(action);
        } else if (action.action === "remove-value") {
            organizationChangeRemove(action);
        }
    };

    const getPlainUserEmail = (user) => {
        return user.email;
    };

    const getInheretedAccessStyle = (user) => {
        return (
            <>
                {user.email}
                {mappedOrganizationAccesses[user.organization?.id] && (
                    <span className="ml-1 text-xxs text-primary-color-orange">
                        ({t("inheritedUserAccessMessage")})
                    </span>
                )}
            </>
        );
    };

    const handleRemoveUser = (index) => {
        let removedUser = selectedUsers[index].value;

        //Remove removed user from list of selected users
        let newSelected = [...selectedUsers];
        if (previousAccess(removedUser?.id)) {
            //if the user already exists in fetched accesses then set isDeleted to true
            newSelected[index].value.isDeleted = true;
        } else {
            //else remove the user from the selectedUsers
            newSelected.splice(index, 1);
        }

        setSelectedUsers(newSelected);

        //Add removed user to users options
        setUsers((prevUsers) => {
            return [
                ...prevUsers,
                {
                    label: getInheretedAccessStyle(removedUser),
                    value: removedUser,
                },
            ];
        });

        setUsersCount((prevUserCount) => prevUserCount - 1);
    };

    const isOwner = (user, returnData) => {
        return user?.value?.creator ? t(toCamelCase(returnData)) : "";
    };

    const previousAccess = (userId) => {
        return modelUserAccesses?.find((access) => access.userId === userId);
    };

    const handleChangeAccessType = (accessType, index) => {
        const isWriteAccess = accessType.value === userAccess.write.value;

        setSelectedUsers((prevSelectedUsers) => {
            const newSelected = [...prevSelectedUsers];
            newSelected[index].value = {
                ...newSelected[index].value,
                read: true,
                write: isWriteAccess,
            };
            return [...newSelected];
        });

        //Renew access dictionary with new access
        setMappedUserAccesses((prevMappedAccesses) => {
            const userId = selectedUsers[index]?.value?.id;
            return {
                ...prevMappedAccesses,
                [userId]: isWriteAccess ? userAccess.write : userAccess.read,
            };
        });
    };

    const renderHeader = () => {
        return (
            headers &&
            headers.map((header, index) => {
                return (
                    <td key={index}>
                        <div className="font-bold px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center justify-center">
                                <span className="ml-2">{header}</span>
                            </div>
                        </div>
                    </td>
                );
            })
        );
    };

    const renderSelectedUsers = () => {
        const usersTable = creator
            ? [...selectedUsers, creator]
            : [...selectedUsers];
        return usersTable.map((user, index) => {
            if (!user.value?.isDeleted) {
                return (
                    <tr key={index}>
                        <td>
                            <div className="flex items-center content-center">
                                <img
                                    src={
                                        user?.value?.image ||
                                        require("../../assets/images/no-avatar.png")
                                            .default
                                    }
                                    className={`${
                                        user?.value?.image ? "" : "opacity-40"
                                    } table-user-img ml-6`}
                                    alt="UserImage"
                                />
                                <div className=" py-3 whitespace-wrap">
                                    {user?.value?.email}
                                </div>
                            </div>
                        </td>
                        <td>
                            <div className="capitalize px-6 py-3 whitespace-wrap">
                                {user?.value?.organization?.name}
                            </div>
                        </td>
                        <td>
                            <div className="px-6 py-3 whitespace-wrap">
                                {isOwner(user, "Owner") || (
                                    <Select
                                        options={Object.values(userAccess)}
                                        value={
                                            mappedUserAccesses[
                                                user?.value?.id
                                            ] || ""
                                        }
                                        styles={getReactSelectSmallStyling(
                                            false
                                        )}
                                        onChange={(selected) => {
                                            handleChangeAccessType(
                                                selected,
                                                index
                                            );
                                        }}
                                        theme={getReactSelectTheme}
                                    />
                                )}
                            </div>
                        </td>
                        <td>
                            <div
                                className={`px-6 py-3 whitespace-wrap ${isOwner(
                                    user,
                                    "disabled"
                                )}`}
                                disabled={isOwner(user, "disabled")}
                            >
                                {isOwner(user, "---") || (
                                    <button
                                        onClick={() => handleRemoveUser(index)}
                                        className="table-btn table-btn-orange m-0"
                                    >
                                        <i className="fas fa-trash-alt" />
                                    </button>
                                )}
                            </div>
                        </td>
                    </tr>
                );
            }
        });
    };

    const content = [
        {
            infoTooltip: t("affiliateAccessDefaultTooltip"),
            required: true,
            label: <Trans i18nKey="affiliates">Affiliates</Trans>,
            input: (
                <Select
                    isMulti
                    value={selectedOrganizations.filter(
                        (organization) => !organization.isDeleted
                    )}
                    options={[...organizationOptions, ...isDeletedOrganization]}
                    isClearable={false}
                    onChange={handleOrganizationChange}
                    className="z-13"
                    placeholder={t("selectPlaceholder", {
                        entity: t("affiliate"),
                    })}
                    theme={getReactSelectTheme}
                    styles={getReactSelectStyling({
                        isMultiSelect: true,
                    })}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                />
            ),
        },
        {
            required: true,
            label: <Trans i18nKey="users">Users</Trans>,
            input: (
                <Select
                    value=""
                    options={users}
                    isClearable
                    onChange={handleOnUsersChange}
                    className="z-12"
                    placeholder={t("selectPlaceholder", {
                        entity: t("users"),
                    })}
                    theme={getReactSelectTheme}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                />
            ),
        },
        {
            element: (
                <SimpleNotification message={t("modelAccessNotification")} />
            ),
        },
        {
            label: (
                <>
                    <Trans i18nKey="giveAccessTo">Give Access To</Trans>
                    <span className="text-primary-orange mx-1">
                        ({usersCount})
                    </span>
                    <Trans i18nKey="users">Users</Trans>
                    <span className="ml-1">
                        <Trans i18nKey="and">and</Trans>
                    </span>
                    <span className="text-primary-orange mx-1">
                        ({organizationCount})
                    </span>
                    <Trans i18nKey="affiliates">affiliates</Trans>
                </>
            ),
        },
        {
            element: (!isEmpty(selectedUsers) || creator) && (
                <div className="h-small-with-scroll table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                    <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center w-full">
                        <thead className="parent-head">
                            <tr>{renderHeader()}</tr>
                        </thead>
                        <tbody>{renderSelectedUsers()}</tbody>
                    </table>
                </div>
            ),
        },
    ];

    const renderContent = () => {
        return (
            content &&
            content.map((field, index) => {
                if (field.element) {
                    return (
                        <React.Fragment key={index}>
                            {field.element}
                        </React.Fragment>
                    );
                }

                return (
                    <div className="input-group" key={index}>
                        <div className="flex">
                            <label className="uppercase">{field.label}</label>
                            {field.infoTooltip && (
                                <div className="text-xs ml-1">
                                    <InformationTooltip
                                        message={field.infoTooltip}
                                    />
                                </div>
                            )}
                        </div>
                        {field.input}
                    </div>
                );
            })
        );
    };

    return (
        <>
            <div className="main-customer-form-inputs">{renderContent()}</div>
        </>
    );
};

export default ModelAccess;
